import { ContentItem, ExportCodeConfig, OutputFormat } from "@perceptron/api/codeGenerators";
import { JSONContent } from "@tiptap/react";

import { env } from "@demo/env";
import { Role } from "@demo/types/base";
import { DocumentInputContentItem } from "@demo/types/documentContent";
import { MediaSet } from "@demo/types/media";
import { DocumentContentProcessor } from "@demo/utils/document-content-processor";

/**
 * Convert DocumentInputContentItem[] to ContentItem[] for export code generation.
 */
function convertDocumentContent(items: DocumentInputContentItem[], urlLookup: Map<string, string>): ContentItem[] {
  const content: ContentItem[] = [];

  for (const item of items) {
    if (item.type === "paragraph") {
      const text = item.value.value;
      if (text.trim()) {
        content.push({ type: "text", text });
      }
    } else if (item.type === "media") {
      for (const media of item.value) {
        const uuid = media.value.uuid;
        const extension = media.value.extension;
        const url = urlLookup.get(uuid) || `<${extension.toUpperCase()}_URL>`;

        switch (media.type) {
          case "video":
            content.push({ type: "video", url });
            break;
          case "audio":
            content.push({ type: "audio", url });
            break;
          case "image":
            content.push({
              type: "image",
              url,
              annotations: media.value.annotations,
            });
            break;
        }
      }
    }
  }

  return content;
}

export interface BuildExportConfigParams {
  mediaSets: MediaSet[];
  getEditorContentAsJSON: (role: Role) => unknown;
  apiKey: string;
  modelName: string;
  outputFormat: OutputFormat;
  temperature?: number;
  enableReasoning: boolean;
  /** The Audio toggle; exported only for a prompt with a video, as the run sends it. */
  enableAudioInVideo?: boolean;
}

/**
 * Build ExportCodeConfig from editor state and media store.
 */
export function buildExportConfig(params: BuildExportConfigParams): ExportCodeConfig {
  const {
    mediaSets,
    getEditorContentAsJSON,
    apiKey,
    modelName,
    outputFormat,
    temperature,
    enableReasoning,
    enableAudioInVideo,
  } = params;

  const processor = new DocumentContentProcessor();

  // Build URL lookup map from media store
  const urlLookup = new Map<string, string>();
  for (const mediaSet of mediaSets) {
    for (const media of mediaSet.media) {
      if (media.url) {
        urlLookup.set(media.key, media.url);
      }
    }
  }

  // Process user content
  const userEditorJSON = getEditorContentAsJSON(Role.USER) as JSONContent;
  const userDocContent = processor.processJsonToContent(userEditorJSON, Role.USER);
  const userContent = convertDocumentContent(userDocContent, urlLookup);

  // If no content, add placeholder
  if (userContent.length === 0) {
    userContent.push({ type: "text", text: "<YOUR_PROMPT>" });
  }

  // Process system content
  const systemEditorJSON = getEditorContentAsJSON(Role.SYSTEM) as JSONContent;
  const systemDocContent = processor.processJsonToContent(systemEditorJSON, Role.SYSTEM);
  const systemContent = convertDocumentContent(systemDocContent, urlLookup);

  return {
    apiUrl: `${env.NEXT_PUBLIC_API_BASE_URL}/v1/chat/completions`,
    apiKey,
    model: modelName,
    outputFormat,
    systemContent: systemContent.length > 0 ? systemContent : undefined,
    userContent,
    temperature,
    enableReasoning,
    // No tools: search runs in the Agent harness, which the export does not target.
    ...(enableAudioInVideo !== undefined && userContent.some((item) => item.type === "video")
      ? { enableAudioInVideo }
      : {}),
  };
}
